import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import { Star } from "lucide-react";
import { fmtValue, fmtTestTime } from "../bodycomp/bodyCompFields";
import {
  DETECTION_ITEMS, SEVERITY_TONE, radarData, detectionPercent, skinValue,
} from "./skinFields";

const SeverityPill = ({ severity, testId }) => {
  if (!severity) return <span className="text-[10px] text-muted-foreground">—</span>;
  const tone = SEVERITY_TONE[severity] || SEVERITY_TONE.Moderate;
  return (
    <span className="inline-flex items-center gap-1" data-testid={testId}>
      <span className="inline-flex">
        {[0, 1, 2].map((i) => (
          <Star
            key={i}
            className="w-3 h-3"
            style={{ color: i < tone.stars ? tone.fg : "#d8d8d2", fill: i < tone.stars ? tone.fg : "#d8d8d2" }}
          />
        ))}
      </span>
      <span
        className="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide"
        style={{ background: tone.bg, color: tone.fg }}
      >
        {severity}
      </span>
    </span>
  );
};

const ShotGallery = ({ images }) => {
  if (!images?.length) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="skin-report-shots-empty">
        No capture shots stored with this report.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="skin-report-shots">
      {images.map((img, i) => (
        <a
          key={img.path || img.url || i}
          href={img.url}
          target="_blank"
          rel="noreferrer"
          className="block rounded-xl overflow-hidden border bg-muted/30 transition-transform hover:-translate-y-0.5"
          data-testid={`skin-report-shot-${i}`}
        >
          <img src={img.url} alt={`Skin analysis capture ${i + 1}`} loading="lazy" className="w-full h-auto" />
        </a>
      ))}
    </div>
  );
};

/** One saved MEICET report, laid out the way the printed sheet reads. */
export const SkinReportView = ({ report }) => {
  if (!report) {
    return (
      <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="skin-report-empty">
        No report yet — upload your Skin Analysis Test Report to see it here.
      </CardContent></Card>
    );
  }

  const score = skinValue(report, "comprehensive_score");
  const skinAge = skinValue(report, "skin_age");
  const tester = report.tester || {};
  const data = radarData(report);

  return (
    <div className="space-y-5" data-testid="skin-report-view">
      <Card className="rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div className="flex items-end gap-3">
              <span className="font-editorial text-4xl leading-none" data-testid="skin-report-score">
                {score === null ? "—" : fmtValue(score, 0)}
              </span>
              <span className="text-xs text-muted-foreground pb-1">Comprehensive Score / 100</span>
            </div>
            <div className="text-right text-[11px] text-muted-foreground">
              <p data-testid="skin-report-test-time">Test Time: {fmtTestTime(report.test_time)}</p>
              {report.report_no && <p data-testid="skin-report-no">Report No.: {report.report_no}</p>}
              {(tester.name || tester.gender || tester.born) && (
                <p data-testid="skin-report-tester">
                  {[tester.name, tester.gender, tester.born].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="skin-report-summary">
            <div className="rounded-xl border bg-muted/30 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Comprehensive Score</p>
              <p className="font-editorial text-xl leading-none mt-1.5" data-testid="skin-summary-comprehensive_score">
                {score === null ? "—" : fmtValue(score, 0)}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/30 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Skin Tone</p>
              <p className="font-editorial text-xl leading-none mt-1.5" data-testid="skin-summary-skin_tone">
                {report.skin_tone || "—"}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/30 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Skin Age (years)</p>
              <p className="font-editorial text-xl leading-none mt-1.5" data-testid="skin-summary-skin_age">
                {skinAge === null ? "—" : fmtValue(skinAge, 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <p className="text-sm font-semibold mb-1">Severity compared to peers</p>
          <p className="text-[11px] text-muted-foreground mb-3">
            A lower percentage means fewer findings than people your age.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-center">
            <div className="h-[280px]" data-testid="skin-report-radar">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data} outerRadius="72%">
                  <PolarGrid stroke="#e3ddd4" />
                  <PolarAngleAxis dataKey="item" tick={{ fontSize: 11, fill: "#6b6b63" }} />
                  <PolarRadiusAxis domain={[0, 100]} angle={90} tick={{ fontSize: 9, fill: "#a8a89f" }} tickCount={5} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 10 }}
                    formatter={(v, _n, p) => [`${fmtValue(v, 2)}%${p?.payload?.severity ? ` · ${p.payload.severity}` : ""}`, p?.payload?.item]}
                  />
                  <Radar
                    dataKey="percent"
                    stroke="#b67358"
                    fill="#b67358"
                    fillOpacity={0.32}
                    isAnimationActive={false}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-xl border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Detection Item</th>
                    <th className="text-right font-medium px-3 py-2">Severity</th>
                    <th className="text-right font-medium px-3 py-2 w-[150px]">Compared to peers</th>
                  </tr>
                </thead>
                <tbody>
                  {DETECTION_ITEMS.map((d) => {
                    const pct = detectionPercent(report, d.key);
                    return (
                      <tr key={d.key} className="border-t">
                        <td className="px-3 py-2">{d.label}</td>
                        <td className="px-3 py-2 text-right font-medium" data-testid={`skin-detection-${d.key}-percent`}>
                          {pct === null ? "—" : `${fmtValue(pct, 2)}%`}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <SeverityPill severity={report?.detections?.[d.key]?.severity} testId={`skin-detection-${d.key}-severity`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="p-5 sm:p-6 space-y-3">
          <div>
            <p className="text-sm font-semibold">Capture shots</p>
            <p className="text-[11px] text-muted-foreground">
              Every light mode captured by the analyser, exactly as printed on your report.
            </p>
          </div>
          <ShotGallery images={report.images} />
          <div>
            <p className="text-sm font-semibold mt-2">Notes</p>
            <p className="text-xs text-muted-foreground" data-testid="skin-report-notes">
              {report.notes || "—"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SkinReportView;
